import React, {
    ChangeEvent,
    FocusEvent,
    SelectHTMLAttributes,
} from "react";

import {
    FieldErrors,
    FieldValues,
    Path,
    RegisterOptions,
    UseFormRegister,
    get,
} from "react-hook-form";

import "./AppSelect_New.scss";

export interface AppSelectOption {
    label: string;
    value: string;
    disabled?: boolean;
}

export interface AppSelect_NewProps<
    TFieldValues extends FieldValues = FieldValues
> extends Omit<
        SelectHTMLAttributes<HTMLSelectElement>,
        "name" | "value" | "onChange" | "onBlur"
    > {
    /**
     * Field label displayed above the select.
     */
    label?: string;

    /**
     * React Hook Form field name.
     */
    name: Path<TFieldValues>;

    /**
     * Options displayed in the dropdown.
     */
    options: AppSelectOption[];

    /**
     * Current selected value.
     */
    value?: string;

    /**
     * Called when selected value changes.
     *
     * AppSelect_New returns the selected string value,
     * rather than the native ChangeEvent.
     */
    onChange?: (value: string) => void;

    /**
     * Called when the select loses focus.
     */
    onBlur?: (event: FocusEvent<HTMLSelectElement>) => void;

    /**
     * React Hook Form register function.
     */
    register?: UseFormRegister<TFieldValues>;

    /**
     * React Hook Form validation rules.
     */
    rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;

    /**
     * React Hook Form errors.
     */
    errors?: FieldErrors<TFieldValues>;

    /**
     * Optional hint displayed below the select.
     */
    hint?: string;

    /**
     * Displays required indicator.
     */
    required?: boolean;

    /**
     * Optional external error message.
     */
    error?: string;

    /**
     * Placeholder displayed as the first option.
     */
    placeholder?: string;

    /**
     * Additional CSS class.
     */
    className?: string;
}

const AppSelect_New = <
    TFieldValues extends FieldValues = FieldValues
>({
    label,
    name,
    options,
    value = "",
    onChange,
    onBlur,
    register,
    rules,
    errors,
    hint,
    required = false,
    error,
    placeholder,
    disabled,
    className = "",
    ...selectProps
}: AppSelect_NewProps<TFieldValues>) => {
    /*
     * React Hook Form registration.
     *
     * We keep register optional so AppSelect_New can be used both:
     *
     * 1. With React Hook Form
     * 2. As a normal controlled component
     */
    const registration = register
        ? register(name, rules)
        : undefined;

    /*
     * Get nested React Hook Form error.
     */
    const fieldError = errors
        ? get(errors, name)
        : undefined;

    const rhfErrorMessage =
        typeof fieldError?.message === "string"
            ? fieldError.message
            : undefined;

    const displayError =
        error || rhfErrorMessage;

    /*
     * Native select change handler.
     *
     * AppSelect_New exposes the selected string value.
     */
    const handleChange = (
        event: ChangeEvent<HTMLSelectElement>
    ) => {
        const selectedValue = event.target.value;

        registration?.onChange(event);

        onChange?.(selectedValue);
    };

    /*
     * Native blur handler.
     */
    const handleBlur = (
        event: FocusEvent<HTMLSelectElement>
    ) => {
        registration?.onBlur(event);

        onBlur?.(event);
    };

    return (
        <div
            className={`app-select-new-wrapper ${className}`}
        >
            {label && (
                <label
                    htmlFor={name}
                    className="app-select-new-label"
                >
                    <span>
                        {label}
                    </span>

                    {required && (
                        <span
                            className="app-select-new-required"
                            aria-hidden="true"
                        >
                            *
                        </span>
                    )}
                </label>
            )}

            <div
                className={`app-select-new-container ${
                    displayError
                        ? "app-select-new-container--error"
                        : ""
                }`}
            >
                <select
                    {...selectProps}
                    {...registration}
                    id={name}
                    name={name}
                    value={value}
                    disabled={disabled}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={displayError ? "true" : "false"}
                    aria-describedby={
                        displayError
                            ? `${name}-error`
                            : hint
                              ? `${name}-hint`
                              : undefined
                    }
                    className="app-select-new"
                >
                    {placeholder && (
                        <option
                            value=""
                            disabled
                        >
                            {placeholder}
                        </option>
                    )}

                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                <span
                    className="app-select-new-arrow"
                    aria-hidden="true"
                >
                    <span />
                </span>
            </div>

            {displayError ? (
                <span
                    id={`${name}-error`}
                    className="app-select-new-error"
                    role="alert"
                >
                    {displayError}
                </span>
            ) : hint ? (
                <span
                    id={`${name}-hint`}
                    className="app-select-new-hint"
                >
                    {hint}
                </span>
            ) : null}
        </div>
    );
};

export default AppSelect_New;